import { WorkReminderModel } from "../../../../factories/work/model/work-reminder.model";
import { SelectOptionType } from "../../../../types/types";
import { ReminderDraft } from "./reminders.mock";

// Same time as email sequences; the server keeps this time zone and recomputes the date
// from the work's current dates (backend ReminderSchedule).
export const REMINDER_SEND_HOUR = 9;

// The API lists the person to notify in notifyUserReminder; userIds is only filled when sending.
export const getReminderUserId = (reminder: WorkReminderModel): string =>
  reminder.userIds?.[0] || reminder.notifyUserReminder?.[0]?.userId || "";

const toLocalDay = (value: Date | string) => {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/** N days before/after the start or due date, at 9:00 AM local time; null if the draft is incomplete. */
export const getReminderDate = (
  draft: Pick<ReminderDraft, "days" | "direction" | "dateBase">,
  work: { startDate?: Date | string | null; dueDate?: Date | string | null },
): Date | null => {
  const days = Number(draft.days);
  const base = draft.dateBase === "dueDate" ? work.dueDate : work.startDate;
  if (!base || !Number.isFinite(days) || days <= 0) return null;

  const day = toLocalDay(base);
  if (Number.isNaN(day.getTime())) return null;

  day.setDate(day.getDate() + (draft.direction === "before" ? -days : days));
  day.setHours(REMINDER_SEND_HOUR, 0, 0, 0);
  return day;
};

/** ISO date with the browser's UTC offset (e.g. 2026-10-26T09:00:00-07:00), so the server knows the time zone. */
export const toIsoWithOffset = (date: Date): string => {
  const pad = (value: number) =>
    `${Math.floor(Math.abs(value))}`.padStart(2, "0");
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}` +
    `${sign}${pad(offset / 60)}:${pad(offset % 60)}`
  );
};

export const formatReminderDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const filterUserOptions = (
  options: SelectOptionType[],
  search: string,
): SelectOptionType[] => {
  const term = search.trim().toLowerCase();
  if (!term) return options;
  return options.filter((option) =>
    `${option.label}`.toLowerCase().includes(term),
  );
};
